
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useTitle from '../hooks/useTitle';
import { fetchSmartnodeData } from '../services/apiService';
import {
  FaServer,
  FaCheckCircle,
  FaQuestionCircle,
  FaEllipsisH,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaMoneyBillWave,
  FaUserShield,
  FaVoteYea,
  FaNetworkWired,
  FaHashtag,
} from 'react-icons/fa';

const SmartNodesPage = () => {
  useTitle("MENELIK - Smart Node");
  const [smartnodeData, setSmartnodeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await fetchSmartnodeData();
      setSmartnodeData(data);
      setLoading(false);
    };

    fetchData();
  }, []);

  if (loading) {
    return <p className="text-center text-lg text-gray-500 my-8">Loading smartnode data...</p>;
  }

  if (!smartnodeData) {
    return <p className="text-center text-lg text-gray-500 my-8">No Smartnode data found.</p>;
  }

  const { summary, list } = smartnodeData;
  const smartnodesArray = Object.entries(list); // [[txid-1, {...nodeData}], ...]

  // expandir/contraer
  const toggleExpand = (key) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6 flex items-center text-gray-800">
        <FaServer className="mr-2 text-blue-600" />
        SmartNodes
      </h1>

      <div className="bg-white shadow-lg rounded-lg p-6 mb-6 border border-gray-200">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-lg text-gray-700">
          <div className="flex items-center bg-gray-50 p-4 rounded shadow-sm">
            <FaServer className="mr-2 text-blue-500" />
            <span>
              <strong>Total: </strong>
              {summary.totalNodes}
            </span>
          </div>

          <div className="flex items-center bg-gray-50 p-4 rounded shadow-sm">
            <FaCheckCircle className="mr-2 text-green-500" />
            <span>
              <strong>Enabled: </strong>
              {summary.enabled}
            </span>
          </div>

          <div className="flex items-center bg-gray-50 p-4 rounded shadow-sm">
            <FaQuestionCircle className="mr-2 text-yellow-500" />
            <span>
              <strong>Unknown: </strong>
              {summary.unknown}
            </span>
          </div>

          <div className="flex items-center bg-gray-50 p-4 rounded shadow-sm">
            <FaEllipsisH className="mr-2 text-red-500" />
            <span>
              <strong>Other: </strong>
              {summary.otherStatuses}
            </span>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">SmartNodes List</h2>
        {smartnodesArray.length > 0 ? (
          <table className="table-auto w-full bg-gray-50 rounded overflow-hidden">
            <thead>
              <tr className="bg-gray-200 text-gray-700">
                <th className="p-3 border-b">Transaction (Key)</th>
                <th className="p-3 border-b">Status</th>
                <th className="p-3 border-b">Last Paid</th>
                <th className="p-3 border-b">Payee</th>
                <th className="p-3 border-b text-center">Expand</th>
              </tr>
            </thead>
            <tbody>
              {smartnodesArray.map(([key, node]) => {
                const isExpanded = expanded[key] || false;
                return (
                  <React.Fragment key={key}>
                    {/* Fila principal */}
                    <tr className="hover:bg-gray-100">
                      <td className="p-3 border-b break-all">
                        {key}
                      </td>
                      <td className="p-3 border-b">
                        <span
                          className={`px-2 py-1 rounded ${
                            node.status === 'ENABLED'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {node.status}
                        </span>
                      </td>
                      <td className="p-3 border-b">
                        <div className="flex items-center text-sm text-gray-600">
                          <FaClock className="mr-1 text-gray-500" />
                          {new Date(node.lastpaidtime * 1000).toLocaleString()} 
                        </div>
                        <div className="flex items-center text-sm text-gray-600 mt-1">
                          <FaHashtag className="mr-1 text-gray-500" />
                          {node.lastpaidblock}
                        </div>
                      </td>
                      <td className="p-3 border-b break-all">
                      <div className="flex items-center">
                        <FaMoneyBillWave className="mr-2 text-blue-500" />
                        <Link 
                          to={`/address/${node.payee}`} 
                          className="text-blue-600 hover:underline break-all"
                        >
                          {node.payee}
                        </Link>
                      </div>

                      </td>
                      <td className="p-3 border-b text-center">
                        <button
                          className={`${
                            isExpanded ? 'bg-blue-600' : 'bg-blue-500'
                          } text-white px-3 py-2 rounded flex items-center mx-auto hover:bg-blue-700 transition-colors`}
                          onClick={() => toggleExpand(key)}
                        >
                          {isExpanded ? (
                            <>
                              <FaChevronUp className="mr-2" />
                              Collapse
                            </>
                          ) : (
                            <>
                              <FaChevronDown className="mr-2" />
                              Expand
                            </>
                          )}
                        </button>
                      </td>
                    </tr>

                    {/* Fila expandida */}
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan="5" className="p-3 border-b">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-700">

                            <div className="p-4 bg-white shadow-sm rounded border border-gray-100">
                              <h3 className="font-semibold mb-2 flex items-center text-gray-800">
                                <FaUserShield className="mr-2 text-blue-500" />
                                Owner Address
                              </h3>
                              <p className="break-all">
                                <Link 
                                  to={`/address/${node.owneraddress}`} 
                                  className="text-blue-600 hover:underline"
                                >
                                  {node.owneraddress}
                                </Link>
                              </p>
                            </div>

                            <div className="p-4 bg-white shadow-sm rounded border border-gray-100">
                              <h3 className="font-semibold mb-2 flex items-center text-gray-800">
                                <FaVoteYea className="mr-2 text-green-500" />
                                Voting Address
                              </h3>
                              <p className="break-all">
                                <Link 
                                  to={`/address/${node.votingaddress}`} 
                                  className="text-green-600 hover:underline"
                                >
                                  {node.votingaddress}
                                </Link>
                              </p>
                            </div>

                            <div className="p-4 bg-white shadow-sm rounded border border-gray-100">
                              <h3 className="font-semibold mb-2 flex items-center text-gray-800">
                                <FaNetworkWired className="mr-2 text-purple-500" />
                                Collateral Address
                              </h3>
                              <p className="break-all">
                                <Link 
                                  to={`/address/${node.collateraladdress}`} 
                                  className="text-purple-600 hover:underline"
                                >
                                  {node.collateraladdress}
                                </Link>
                              </p>
                            </div>

                            <div className="p-4 bg-white shadow-sm rounded border border-gray-100 md:col-span-2">
                              <h3 className="font-semibold mb-2 flex items-center text-gray-800">
                                <FaHashtag className="mr-2 text-gray-500" />
                                PubKeyOperator
                              </h3>
                              <p className="break-all">{node.pubkeyoperator}</p>
                            </div>

                            <div className="p-4 bg-white shadow-sm rounded border border-gray-100">
                              <h3 className="font-semibold mb-2 flex items-center text-gray-800">
                                PoSe Penalty
                              </h3>
                              <p className="text-lg">{node.posepenalty}</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500">No smartnodes found.</p>
        )}
      </div>
    </div>
  );
};

export default SmartNodesPage;
